// The /auctions list's one query model (BL-2769, ADR 059 — Search Spine § 2.5): the route, the
// counts route and the offline device-store answer all read the URL through this, so no two
// readers can disagree on what a status, rep, date or sort value means.

import {
  DEFAULT_LIST_PAGE_SIZE,
  normalizeInventoryFilterList,
  normalizeListSearchTerm,
  normalizeListTextParam,
  parseListInstant,
  parseListLimit,
  parseListOffset,
  type ListLimitBounds,
} from './list-query-params';
import { parseAuctionStatusKey, type AuctionStatusKey } from '../auctions/auction-status-display';

/**
 * The list's page window: ADR 059 — Search Spine § 2.5's default of 50. 200 is the ceiling the
 * "every auction" pickers read in (the relist picker, the Storage sheet).
 */
export const AUCTION_LIST_LIMIT_BOUNDS: ListLimitBounds = { defaultLimit: DEFAULT_LIST_PAGE_SIZE, maxLimit: 200 };

/** The Status column's sort keys are the Auctions table's column keys that sort on the server. */
export const AUCTION_LIST_SORT_COLUMNS = ['title', 'sellers', 'type', 'status', 'bidders', 'startDate', 'lots', 'createdAt'] as const;
export type AuctionListSortColumn = (typeof AUCTION_LIST_SORT_COLUMNS)[number];

/** Which date the range reads. */
export const AUCTION_LIST_DATE_FIELDS = ['startDate', 'firstLotCloseAt', 'createdAt'] as const;
export type AuctionListDateField = (typeof AUCTION_LIST_DATE_FIELDS)[number];

/**
 * Every status key in lifecycle order: the Status column sorts by it (BL-1995) and the Status
 * pill lists its options in it. `deleted` is last.
 */
export const AUCTION_STATUS_KEY_ORDER: readonly AuctionStatusKey[] = [
  'draft',
  'ready',
  'accepting-pre-bids',
  'active',
  'paused',
  'closed',
  'closed-stage-1',
  'closed-stage-2',
  'closed-stage-3',
  'closed-stage-4',
  'deleted',
];

/** What a list request asks for, normalized: a malformed value is the default, never a 500. */
export interface AuctionListQuery {
  /** The trimmed, capped term, or null for no search. Escaped once, in the repository. */
  search: string | null;
  /** Live status keys; empty is every status. Never holds `deleted` (see `archived`). */
  statuses: AuctionStatusKey[];
  /** True when the Status pill picked `deleted`: the soft-deleted set, with no other status. */
  archived: boolean;
  /** Staff-user ids and/or `SALES_REP_FILTER_HOUSE`; empty is every rep. */
  salesReps: string[];
  dateField: AuctionListDateField;
  /** The range's inclusive start, resolved to an instant client-side, or null. */
  dateFrom: string | null;
  /** The range's inclusive end, or null. */
  dateTo: string | null;
  sortColumn: AuctionListSortColumn | null;
  sortDirection: 'asc' | 'desc';
  limit: number;
  offset: number;
}

/**
 * Read an `/api/auctions` query string.
 *
 * @param params - The request's query string.
 * @returns The normalized query.
 */
export function readAuctionListQuery(params: URLSearchParams): AuctionListQuery {
  const keys = (normalizeInventoryFilterList(params.get('status')) ?? [])
    .map((raw) => parseAuctionStatusKey(raw))
    .flatMap((parsed) => (parsed ? [parsed] : []))
    .map((parsed) => (parsed.phase === 'closed' && parsed.closeStage > 0 ? (`closed-stage-${parsed.closeStage}` as AuctionStatusKey) : parsed.phase));
  // `deleted` reads a different data set and never mixes with a live status (as the pill's pick rule).
  const archived = keys.includes('deleted');
  const statuses = archived ? [] : [...new Set(keys)];
  const sortColumn = (AUCTION_LIST_SORT_COLUMNS as readonly string[]).find((c) => c === normalizeListTextParam(params.get('sortColumn'))) as
    | AuctionListSortColumn
    | undefined;
  const dateField = (AUCTION_LIST_DATE_FIELDS as readonly string[]).find((f) => f === params.get('dateField')) as AuctionListDateField | undefined;
  return {
    search: normalizeListSearchTerm(params.get('search')),
    statuses,
    archived,
    salesReps: normalizeInventoryFilterList(params.get('salesRepId')) ?? [],
    dateField: dateField ?? 'startDate',
    dateFrom: parseListInstant(params.get('dateFrom')),
    dateTo: parseListInstant(params.get('dateTo')),
    sortColumn: sortColumn ?? null,
    sortDirection: params.get('sortDirection') === 'asc' ? 'asc' : 'desc',
    limit: parseListLimit(params.get('limit'), AUCTION_LIST_LIMIT_BOUNDS),
    offset: parseListOffset(params.get('offset')),
  };
}
